import React from "react";
import { createPortal } from "react-dom";
import { resolvePublicImageUrl } from "../../../helper/helper";
import ImageCropModal from "../../../components/ImageCropModal/ImageCropModal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type { SubmitHandler } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Api } from "../../../services/interceptor";
import { ProfileDatePicker } from "../../profileOnboarding/components/ProfileDatePicker";
import {
  BALL_TYPES,
  type BallType,
  DEFAULT_TOURNAMENT_RULES,
  getBallTypeLabel,
  type ICreateTournamentUI,
  LEGACY_LIMITED_OVER_TYPES,
  MATCH_TYPES,
  MatchTypeLabel,
  schema,
  TOURNAMENT_CATEGORIES,
  TOURNAMENT_GENDERS,
  TournamentGenderLabel,
  type TournamentGender,
  type MatchType,
  type TournamentCategory,
  type TournamentRulesState,
} from "./types/createtournament";
import type { IGetAllTournaments } from "../types/home";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { updateTournamentRequest } from "./api/updateTournamentApi";
import TournamentTickerConfigSection from "./components/TournamentTickerConfigSection";
import WaiverSettingsSection from "./components/WaiverSettingsSection";
import type { TickerRowState } from "./tournamentsDetails/matches/tickerAutomationConstants";
import "./UpdateTournamentModal.scss";

type ImageTarget = "banner" | "logo";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function toDateInput(d: string): string {
  if (!d) return "";
  return d.includes("T") ? d.slice(0, 10) : d.slice(0, 10);
}

function mapBallTypeFromApi(s: string): BallType {
  const u = (s || "").toLowerCase();
  if (u.includes("tennis")) return "BallType.tennis";
  if (u.includes("other")) return "BallType.other";
  return "BallType.leather";
}

function toMatchTypeKey(mt: string): string {
  return (mt || "").trim().toUpperCase().replace(/\s+/g, "_");
}

/** Legacy T10 / ODI / OTHER_LIMITED_OVER open as LIMITED_OVERS, like the app. */
function normalizeMatchType(mt: string): MatchType {
  const u = toMatchTypeKey(mt);
  if (u === "TEST_MATCH") return "TEST";
  if ((MATCH_TYPES as readonly string[]).includes(u)) return u as MatchType;
  if (mt?.toUpperCase().includes("TEST")) return "TEST";
  return "LIMITED_OVERS";
}

function normalizeCategory(cat: string): TournamentCategory {
  const u = (cat || "").toUpperCase();
  if (TOURNAMENT_CATEGORIES.includes(u as TournamentCategory)) return u as TournamentCategory;
  return "OTHER";
}


function tournamentToFormDefaults(
  t: IGetAllTournaments,
  leagueId: string,
): ICreateTournamentUI {
  const ccRaw = (t.organizerPhoneNumberCountryCode || "91").replace(/\D/g, "") || "91";
  const digits = (t.organizerPhoneNumber || "").replace(/\D/g, "");
  // Stored as the national number; older rows may carry the dial code in front. Never cut a longer number down.
  const national = digits.length > 10 && digits.startsWith(ccRaw) ? digits.slice(ccRaw.length) : digits;
  const fullPhone = national ? `${ccRaw}${national}` : "";
  const isLegacyLimitedOvers = toMatchTypeKey(t.matchType) in LEGACY_LIMITED_OVER_TYPES;
  const legacyOvers = LEGACY_LIMITED_OVER_TYPES[toMatchTypeKey(t.matchType)];

  return {
    leagueId: t.leagueId || leagueId,
    bannerUri: t.bannerUri,
    canEditTournament: t.canEditTournament,
    isRosterLocked: t.isRosterLocked,
    canEditScoreCard: t.canEditScoreCard,
    isImpactPlayerAllowed: t.isImpactPlayerAllowed,
    tournamentName: t.tournamentName,
    description: t.description ?? "",
    organizerName: t.organizerName,
    organizerEmailId: t.organizerEmailId ?? "",
    organizerPhoneNumber: fullPhone,
    organizerPhoneNumberCountryCode: ccRaw,
    startDate: toDateInput(t.startDate),
    endDate: toDateInput(t.endDate),
    matchType: normalizeMatchType(t.matchType),
    maxOversPerBowler: t.maxOversPerBowler,
    pointForWin: t.pointForWin,
    pointForTie: t.pointForTie,
    pointForAbandoned: t.pointForAbandoned,
    pointForFirstInningsLead: t.pointForFirstInningsLead,
    pointForFirstInningsLoss: t.pointForFirstInningsLoss,
    pointForOutrightWin: t.pointForOutrightWin,
    bonusPointForWinByInnings: t.bonusPointForWinByInnings,
    ballType: mapBallTypeFromApi(t.ballType),
    tournamentCategory: normalizeCategory(t.tournamentCategory),
    maxOvers: t.maxOvers > 0 ? t.maxOvers : legacyOvers,
    gender: (TOURNAMENT_GENDERS as readonly string[]).includes((t.gender ?? "").toUpperCase()) ? (t.gender!.toUpperCase() as TournamentGender) : null,
    country: t.country ?? "",
    maxWickets: t.maxWickets > 0 ? t.maxWickets : isLegacyLimitedOvers ? 10 : undefined,
    bonusPointsEnabled: t.bonusPointsEnabled ?? false,
    isStrictModeEnabled: t.strictModeEnabled ?? false,
    isTickerAutomationEnabled: t.isTickerAutomationEnabled ?? t.tickerAutomationEnabled ?? true,
    waiverRequired: t.waiverRequired ?? false,
    waiverId: t.waiverId ?? null,
  };
}

/** Country names for the Country field (the app sends the name, e.g. "United States"). */
const COUNTRY_NAMES: string[] = (() => {
  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    const names = new Set<string>();
    for (let a = 65; a <= 90; a++) for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      const name = dn.of(code);
      if (name && name !== code && !/unknown|europe|world|outlying|union|nations|pseudo/i.test(name)) names.add(name);
    }
    return [...names].sort((x, y) => x.localeCompare(y));
  } catch {
    return [];
  }
})();

/** Multi-day only (the backend ignores match days for other formats). "Not set" can't be picked, only shown. */
const MATCH_DAY_OPTIONS = [
  { value: "0", label: "Not set" },
  { value: "1", label: "1 day" },
  { value: "2", label: "2 days" },
  { value: "3", label: "3 days" },
  { value: "4", label: "4 days" },
  { value: "5", label: "5 days" },
  { value: "6", label: "6 days" },
  { value: "7", label: "7 days" },
];

type Props = {
  open: boolean;
  tournament: IGetAllTournaments | null;
  leagueId: string | null;
  onOpenChange: (open: boolean) => void;
};

const UpdateTournamentModal: React.FC<Props> = ({ open, tournament, leagueId, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [scheduledDays, setScheduledDays] = useState("0");
  const [tickerAutomationEnabled, setTickerAutomationEnabled] = useState(true);
  const [tickerRows, setTickerRows] = useState<TickerRowState[]>([]);
  const [waiverRequired, setWaiverRequired] = useState(false);
  const [waiverId, setWaiverId] = useState<string | null>(null);
  const [waiverError, setWaiverError] = useState<string | undefined>(undefined);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  // Clear the image already saved on the tournament (sent as removeBannerImage / removeLogoImage).
  const [removeBanner, setRemoveBanner] = useState(false);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [tournamentRules, setTournamentRules] = useState<TournamentRulesState>(DEFAULT_TOURNAMENT_RULES);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState<ImageTarget>("banner");
  const [cropSrc, setCropSrc] = useState('');
  const [cropMimeType, setCropMimeType] = useState('image/jpeg');
  const [cropFileName, setCropFileName] = useState('banner');

  const resetImageState = () => {
    setBannerFile(null);
    setBannerPreviewUrl(null);
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setRemoveBanner(false);
    setRemoveLogo(false);
  };

  const effectiveLeagueId = leagueId ?? tournament?.leagueId ?? null;

  // Current per-graphic ticker setup, so the rows open as saved (they used to open all off and were never saved).
  const tickerQuery = useQuery({
    queryKey: ["tournamentTickerConfig", tournament?.tournamentId],
    enabled: open && !!tournament?.tournamentId,
    staleTime: 0,
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.tournamentTickerConfig}/${tournament!.tournamentId}/ticker-config`);
      const list = (Array.isArray(res?.data) ? res.data : []) as Array<Record<string, unknown>>;
      return list.map((c): TickerRowState => ({
        graphicType: String(c.graphicType),
        graphicTitle: "",
        automated: Boolean(c.automated ?? c.isAutomated),
        intervalType: (c.intervalType as TickerRowState["intervalType"]) ?? "Over",
        intervalValue: Number(c.intervalValue ?? 1),
        hasInterval: true,
      }));
    },
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        resetImageState();
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !tournament) return;
    const sd = tournament.scheduledDays;
    if (sd != null && sd >= 0) setScheduledDays(String(sd));
    else setScheduledDays("0");
    const tickerEnabled = tournament.isTickerAutomationEnabled ?? tournament.tickerAutomationEnabled;
    if (typeof tickerEnabled === "boolean") {
      setTickerAutomationEnabled(tickerEnabled);
    }
    setWaiverRequired(!!tournament.waiverRequired);
    setWaiverId(tournament.waiverId ?? null);
    setWaiverError(undefined);
    if (tournament.tournamentRule) {
      setTournamentRules({
        countWideBall: tournament.tournamentRule.countWideBall,
        countNoBall: tournament.tournamentRule.countNoBall,
        runsForWideBall: tournament.tournamentRule.runsForWideBall,
        runsForNoBall: tournament.tournamentRule.runsForNoBall,
        excludeRules: tournament.tournamentRule.excludeRules ?? [],
        excludeOvers: tournament.tournamentRule.excludeOvers ?? [],
      });
    } else {
      setTournamentRules(DEFAULT_TOURNAMENT_RULES);
    }
    resetImageState();
    setCropOpen(false);
    setCropSrc('');
  }, [
    open,
    tournament?.tournamentId,
    tournament?.scheduledDays,
    tournament?.isTickerAutomationEnabled,
    tournament?.tickerAutomationEnabled,
  ]);

  const formDefaults = useMemo((): ICreateTournamentUI => {
    if (!tournament || !effectiveLeagueId) {
      return {
        leagueId: null,
        bannerUri: null,
        canEditTournament: null,
        isRosterLocked: false,
        canEditScoreCard: false,
        isImpactPlayerAllowed: false,
        tournamentName: "",
        description: "",
        organizerName: "",
        organizerPhoneNumber: "",
        organizerPhoneNumberCountryCode: "",
        organizerEmailId: "",
        startDate: "",
        endDate: "",
        matchType: "T20",
        ballType: "BallType.leather",
        tournamentCategory: "OTHER",
        maxOversPerBowler: 1,
        pointForWin: 0,
        pointForTie: 0,
        pointForAbandoned: 0,
        bonusPointsEnabled: false,
        isStrictModeEnabled: false,
        isTickerAutomationEnabled: true,
        waiverRequired: false,
        waiverId: null,
      };
    }
    return tournamentToFormDefaults(tournament, effectiveLeagueId);
  }, [tournament, effectiveLeagueId]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ICreateTournamentUI, unknown, ICreateTournamentUI>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues: formDefaults,
  });

  useEffect(() => {
    if (open && tournament) {
      reset(formDefaults);
    }
  }, [open, tournament?.tournamentId]);

  const matchTypeWatch = watch("matchType");
  const ballTypeWatch = watch("ballType");
  const categoryWatch = watch("tournamentCategory");
  const genderWatch = watch("gender");
  const maxOversWatch = watch("maxOvers");
  const overCount = (() => {
    switch (matchTypeWatch) {
      case "T20": return 20;
      case "THE_HUNDRED": return 20;
      case "INDOOR": return 10;
      case "LIMITED_OVERS":
        return typeof maxOversWatch === "number" && maxOversWatch > 0 ? maxOversWatch : 20;
      default: return 20;
    }
  })();

  // Excluded overs belong to the old over count, so they're cleared when the user picks another format (in the
  // chip handler). An effect on matchType used to do this and also ran on open, wiping the saved selection.

  const fc = (hasErr?: boolean) => `form-control ${hasErr ? "field-error" : ""}`;

  const handleOpenChange = (next: boolean) => {
    if (!next) resetImageState();
    onOpenChange(next);
  };

  const onInvalid = (formErrors: Record<string, unknown>) => {
    console.error("Update tournament validation failed:", formErrors);
    const fieldNames = Object.keys(formErrors);
    toast.error(
      fieldNames.length
        ? `Please fix: ${fieldNames.join(", ")}`
        : "Please fix the highlighted fields before saving.",
    );
    setTimeout(() => {
      const firstErrorField = document.querySelector<HTMLElement>(
        ".update-tournament-modal-form .field-error, .update-tournament-modal-form .err-msg",
      );
      firstErrorField?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const onSave: SubmitHandler<ICreateTournamentUI> = async (data) => {
    if (!tournament?.tournamentId || !effectiveLeagueId) return;
    if (waiverRequired && !waiverId) {
      setWaiverError("Please select a waiver form");
      return;
    }
    setWaiverError(undefined);
    setSubmitting(true);
    try {
      const draft: Record<string, unknown> = {
        ...data,
        leagueId: effectiveLeagueId,
        scheduledDays: Number(scheduledDays) || 0,
        isTickerAutomationEnabled: tickerAutomationEnabled,
        // Not edited here: echo what GET returned (powerplay comes back as enablePowerplay / maxPowerplayOvers).
        // Anything the response didn't include is left out so the backend keeps the stored value.
        isAbandonedGameCounted: tournament.isAbandonedGameCounted,
        rankingPreference: tournament.rankingPreference,
        isPowerPlayEnabled: tournament.enablePowerplay ?? tournament.isPowerPlayEnabled,
        maxPowerPlayOvers: tournament.maxPowerplayOvers ?? tournament.maxPowerPlayOvers,
        waiverRequired,
        waiverId,
        tournamentRules,
        removeBannerImage: removeBanner && !bannerFile,
        removeLogoImage: removeLogo && !logoFile,
      };
      await updateTournamentRequest({
        tournamentId: tournament.tournamentId,
        leagueId: effectiveLeagueId,
        draft,
        bannerFile,
        logoFile,
      });
      // Same call the create flow makes; only when the saved setup loaded, so a failed load can't switch graphics off.
      if (tickerAutomationEnabled && tickerQuery.isSuccess && tickerRows.length > 0) {
        await Api.put(
          `${QUERY_KEY.tournamentTickerConfig}/${tournament.tournamentId}/ticker-config`,
          { graphicConfigs: tickerRows.map((r) => ({ graphicType: r.graphicType, automated: r.automated, intervalType: r.intervalType, intervalValue: r.intervalValue, duration: r.intervalValue })) },
          { headers: { "Content-Type": "application/json" } },
        );
        void queryClient.invalidateQueries({ queryKey: ["tournamentTickerConfig", tournament.tournamentId] });
      }
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTournamentsByLeagues, effectiveLeagueId],
      });
      toast.success("Tournament updated successfully.");
      handleOpenChange(false);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: unknown } };
      const d = ax?.response?.data;
      const message =
        typeof d === "string"
          ? d
          : d && typeof d === "object" && "message" in d
            ? String((d as { message: unknown }).message)
            : null;
      toast.error(message || "Failed to update tournament. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (target: ImageTarget) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be 5MB or smaller.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropTarget(target);
      setCropSrc(reader.result as string);
      setCropMimeType(file.type || 'image/jpeg');
      setCropFileName(file.name);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = (croppedFile: File, previewUrl: string) => {
    setCropOpen(false);
    if (cropTarget === "logo") {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoFile(croppedFile);
      setLogoPreviewUrl(previewUrl);
    } else {
      if (bannerPreviewUrl) URL.revokeObjectURL(bannerPreviewUrl);
      setBannerFile(croppedFile);
      setBannerPreviewUrl(previewUrl);
    }
  };

  const clearNewImage = (target: ImageTarget) => {
    if (target === "logo") {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoFile(null);
      setLogoPreviewUrl(null);
    } else {
      if (bannerPreviewUrl) URL.revokeObjectURL(bannerPreviewUrl);
      setBannerFile(null);
      setBannerPreviewUrl(null);
    }
  };

  if (!open || !tournament) {
    return null;
  }

  return createPortal(
    <>
    <ImageCropModal
      open={cropOpen}
      imageSrc={cropSrc}
      aspect={cropTarget === "logo" ? 1 : 16 / 9}
      mimeType={cropMimeType}
      fileName={cropFileName}
      onCrop={handleCropConfirm}
      onCancel={() => setCropOpen(false)}
    />
    <div
      className="update-tournament-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-tournament-modal-title"
    >
      <button
        type="button"
        className="update-tournament-modal-overlay"
        aria-label="Close dialog"
        onClick={() => handleOpenChange(false)}
      />
      <div
        className="update-tournament-modal-content"
        role="document"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="update-tournament-modal-header">
          <h2 id="update-tournament-modal-title" className="update-tournament-modal-title">
            Update Tournament
          </h2>
          <button type="button" className="update-tournament-modal-close" aria-label="Close" onClick={() => handleOpenChange(false)}>
            ×
          </button>
        </div>

        {effectiveLeagueId ? (
            <form className="update-tournament-modal-form" onSubmit={handleSubmit(onSave, onInvalid)}>
              <div className="form-control-wrap">
                <div className="form-group">
                  <label>
                    Tournament Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Tournament Name"
                    className={fc(!!errors.tournamentName)}
                    {...register("tournamentName")}
                  />
                  {errors.tournamentName && <p className="err-msg">{errors.tournamentName.message}</p>}
                </div>
                <div className="form-group">
                  <label>
                    Mobile Number <span className="req">*</span>
                  </label>
                  <div className="phone">
                    <Controller
                      control={control}
                      name="organizerPhoneNumber"
                      render={({ field }) => (
                        <PhoneInput
                          country="in"
                          value={field.value ?? ""}
                          countryCodeEditable={false}
                          onChange={(value, country: { dialCode?: string }) => {
                            field.onChange(value);
                            setValue("organizerPhoneNumberCountryCode", country?.dialCode || "", {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                          inputClass={`${fc(!!errors.organizerPhoneNumber)} phone-input-field`}
                          containerClass="phone-input-container"
                          placeholder="Enter Mobile Number"
                          enableSearch
                        />
                      )}
                    />
                    <input type="hidden" {...register("organizerPhoneNumberCountryCode")} />
                  </div>
                  {(errors.organizerPhoneNumber || errors.organizerPhoneNumberCountryCode) && (
                    <p className="err-msg">
                      {errors.organizerPhoneNumber?.message ||
                        errors.organizerPhoneNumberCountryCode?.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="update-tournament-dates-block">
                <label className="update-tournament-dates-label" htmlFor="upd-tournament-start">
                  Tournament Dates <span className="req">*</span>
                </label>
                <div className="date-range-pair">
                  <div className="date-field">
                    <Controller
                      control={control}
                      name="startDate"
                      render={({ field }) => (
                        <ProfileDatePicker
                          id="upd-tournament-start"
                          className="update-tournament-date-picker"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          placeholder="Start Date"
                          hasError={!!errors.startDate}
                        />
                      )}
                    />
                    {errors.startDate && <p className="err-msg">{errors.startDate.message}</p>}
                  </div>
                  <div className="date-field">
                    <Controller
                      control={control}
                      name="endDate"
                      render={({ field }) => (
                        <ProfileDatePicker
                          id="upd-tournament-end"
                          className="update-tournament-date-picker"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          placeholder="End Date"
                          hasError={!!errors.endDate}
                        />
                      )}
                    />
                    {errors.endDate && <p className="err-msg">{errors.endDate.message}</p>}
                  </div>
                </div>
              </div>

              <div className="form-control-wrap">
                <div className="form-group">
                  <label>
                    Organiser Email <span className="req">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Enter Email Id"
                    className={fc(!!errors.organizerEmailId)}
                    {...register("organizerEmailId")}
                  />
                  {errors.organizerEmailId && <p className="err-msg">{errors.organizerEmailId.message}</p>}
                </div>
                <div className="form-group">
                  <label>
                    Organiser name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Organiser Name"
                    className={fc(!!errors.organizerName)}
                    {...register("organizerName")}
                  />
                  {errors.organizerName && <p className="err-msg">{errors.organizerName.message}</p>}
                </div>
              </div>

              <div className="score-rule-wrap update-tournament-score-rule">
                {matchTypeWatch !== "TEST" && (
                  <div className="over-bolwer">
                    <label>
                      Overs per Bowler <span className="req">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="Enter overs"
                      className={fc(!!errors.maxOversPerBowler)}
                      {...register("maxOversPerBowler", { valueAsNumber: true })}
                    />
                    {errors.maxOversPerBowler && (
                      <p className="err-msg">{errors.maxOversPerBowler.message}</p>
                    )}
                  </div>
                )}
                <div className="points-data-wrap">
                  {matchTypeWatch !== "TEST" && (
                    <div className="points-data">
                      <label>Points for Win</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForWin)}
                        {...register("pointForWin", { valueAsNumber: true })}
                      />
                      {errors.pointForWin && <p className="err-msg">{errors.pointForWin.message}</p>}
                    </div>
                  )}
                  <div className="points-data">
                    <label>Points for Tie</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForTie)}
                      {...register("pointForTie", { valueAsNumber: true })}
                    />
                    {errors.pointForTie && <p className="err-msg">{errors.pointForTie.message}</p>}
                  </div>
                  <div className="points-data">
                    <label>Points for Abandoned</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForAbandoned)}
                      {...register("pointForAbandoned", { valueAsNumber: true })}
                    />
                    {errors.pointForAbandoned && (
                      <p className="err-msg">{errors.pointForAbandoned.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="match-type-wrapper">
                <div className="match-type">
                  <label>Match Type</label>
                  <div className="chip-group">
                    {MATCH_TYPES.map((mt) => (
                      <button
                        type="button"
                        key={mt}
                        className={`chip ${matchTypeWatch === mt ? "selected" : ""}`}
                        onClick={() => {
                          if (mt !== matchTypeWatch) setTournamentRules((r) => ({ ...r, excludeOvers: [] }));
                          setValue("matchType", mt, { shouldValidate: true, shouldDirty: true });
                        }}
                      >
                        {MatchTypeLabel[mt]}
                      </button>
                    ))}
                  </div>
                  {errors.matchType && <p className="err-msg">{errors.matchType.message as string}</p>}
                </div>
                <div className="ball-type">
                  <label>Ball Type</label>
                  <div className="chip-group">
                    {BALL_TYPES.map((bt) => (
                      <button
                        type="button"
                        key={bt}
                        className={`chip ${ballTypeWatch === bt ? "selected" : ""}`}
                        onClick={() =>
                          setValue("ballType", bt, { shouldValidate: true, shouldDirty: true })
                        }
                      >
                        {getBallTypeLabel(bt as BallType)}
                      </button>
                    ))}
                  </div>
                  {errors.ballType && <p className="err-msg">{errors.ballType.message as string}</p>}
                </div>
              </div>

              {matchTypeWatch === "LIMITED_OVERS" && (
                <div className="other-limited-wrap">
                  <div className="over-wrap">
                    <label>
                      No. Of Overs <span className="req">*</span>
                    </label>
                    <input
                      className={fc(!!errors.maxOvers)}
                      placeholder="Enter Overs"
                      {...register("maxOvers", { valueAsNumber: true })}
                    />
                    {errors.maxOvers && <p className="err-msg">{errors.maxOvers.message as string}</p>}
                  </div>
                  <div className="wicket-wrap">
                    <label>
                      No. Of Wickets <span className="req">*</span>
                    </label>
                    <input
                      className={fc(!!errors.maxWickets)}
                      placeholder="Enter wickets"
                      {...register("maxWickets", { valueAsNumber: true })}
                    />
                    {errors.maxWickets && (
                      <p className="err-msg">{errors.maxWickets.message as string}</p>
                    )}
                  </div>
                </div>
              )}

              {matchTypeWatch === "TEST" && (
                <div style={{ marginTop: "16px" }}>
                  <div className="form-control-wrap">
                    <div className="form-group">
                      <label>Overs per Innings</label>
                      <input
                        type="number"
                        min={1}
                        placeholder="90"
                        className={fc(!!errors.maxOvers)}
                        {...register("maxOvers", { valueAsNumber: true })}
                      />
                      {errors.maxOvers && <p className="err-msg">{errors.maxOvers.message as string}</p>}
                    </div>
                    <div className="form-group update-tournament-match-days">
                      <label>Match Days</label>
                      <select
                        className="form-control"
                        value={scheduledDays}
                        onChange={(e) => setScheduledDays(e.target.value)}
                      >
                        {MATCH_DAY_OPTIONS.map((o) => (
                          <option key={o.value || "empty"} value={o.value} disabled={o.value === "0"}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-control-wrap">
                    <div className="form-group">
                      <label>Points for First Innings Lead</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForFirstInningsLead)}
                        {...register("pointForFirstInningsLead", { valueAsNumber: true })}
                      />
                      {errors.pointForFirstInningsLead && (
                        <p className="err-msg">{errors.pointForFirstInningsLead.message}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Points for First Innings Loss</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForFirstInningsLoss)}
                        {...register("pointForFirstInningsLoss", { valueAsNumber: true })}
                      />
                      {errors.pointForFirstInningsLoss && (
                        <p className="err-msg">{errors.pointForFirstInningsLoss.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-control-wrap">
                    <div className="form-group">
                      <label>Points for Outright Win</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForOutrightWin)}
                        {...register("pointForOutrightWin", { valueAsNumber: true })}
                      />
                      {errors.pointForOutrightWin && (
                        <p className="err-msg">{errors.pointForOutrightWin.message}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Bonus Points for Win by Innings</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.bonusPointForWinByInnings)}
                        {...register("bonusPointForWinByInnings", { valueAsNumber: true })}
                      />
                      {errors.bonusPointForWinByInnings && (
                        <p className="err-msg">{errors.bonusPointForWinByInnings.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Wide/No Ball Rules — shown for all match types */}
              <div style={{ marginTop: "16px" }}>
                <hr className="soft-sep" />
                <h2 className="sec-title">Wide/No Ball Rules</h2>

                <div className="tournament-rules-card">
                  <div className="tournament-rule-row">
                    <span className="rule-badge">A</span>
                    <span className="rule-label">Count wide as a legal delivery</span>
                    <label className="toggle" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={tournamentRules.countWideBall}
                        onChange={(e) => setTournamentRules((r) => ({ ...r, countWideBall: e.target.checked }))}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                  <div className="tournament-rule-row">
                    <span className="rule-badge">B</span>
                    <span className="rule-label">Wide</span>
                    <div className="rule-counter">
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForWideBall: Math.max(0, r.runsForWideBall - 1) }))}>−</button>
                      <span className="counter-value">{tournamentRules.runsForWideBall}</span>
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForWideBall: r.runsForWideBall + 1 }))}>+</button>
                    </div>
                  </div>
                  <div className="tournament-rule-row">
                    <span className="rule-badge">C</span>
                    <span className="rule-label">Count no ball as a legal delivery</span>
                    <label className="toggle" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={tournamentRules.countNoBall}
                        onChange={(e) => setTournamentRules((r) => ({ ...r, countNoBall: e.target.checked }))}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                  <div className="tournament-rule-row">
                    <span className="rule-badge">D</span>
                    <span className="rule-label">No Ball</span>
                    <div className="rule-counter">
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForNoBall: Math.max(0, r.runsForNoBall - 1) }))}>−</button>
                      <span className="counter-value">{tournamentRules.runsForNoBall}</span>
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForNoBall: r.runsForNoBall + 1 }))}>+</button>
                    </div>
                  </div>
                </div>

                {matchTypeWatch !== "TEST" && (
                  <div className="tournament-rules-meta">
                    <div className="rules-meta-row">
                      <span className="rules-meta-label">Ignore rules</span>
                      <div className="rules-chips">
                        {['A', 'B', 'C', 'D'].map((rule) => (
                          <button
                            type="button"
                            key={rule}
                            className={`rules-chip ${tournamentRules.excludeRules.includes(rule) ? 'selected' : ''}`}
                            onClick={() =>
                              setTournamentRules((r) => ({
                                ...r,
                                excludeRules: r.excludeRules.includes(rule)
                                  ? r.excludeRules.filter((x) => x !== rule)
                                  : [...r.excludeRules, rule],
                              }))
                            }
                          >{rule}</button>
                        ))}
                      </div>
                    </div>
                    <div className="rules-meta-row">
                      <span className="rules-meta-label">For overs</span>
                      <div className="rules-chips overs-chips">
                        {Array.from({ length: overCount }, (_, i) => i + 1).map((over) => (
                          <button
                            type="button"
                            key={over}
                            className={`rules-chip ${tournamentRules.excludeOvers.includes(over) ? 'selected' : ''}`}
                            onClick={() =>
                              setTournamentRules((r) => ({
                                ...r,
                                excludeOvers: r.excludeOvers.includes(over)
                                  ? r.excludeOvers.filter((x) => x !== over)
                                  : [...r.excludeOvers, over],
                              }))
                            }
                          >{over}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="tournament-category-wrapper">
                <div className="category-wrap">
                  <label>Tournament Category</label>
                  <div className="chip-group">
                    {TOURNAMENT_CATEGORIES.map((cat) => (
                      <button
                        type="button"
                        key={cat}
                        className={`chip ${categoryWatch === cat ? "selected" : ""}`}
                        onClick={() =>
                          setValue("tournamentCategory", cat, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                      >
                        {cat.charAt(0) + cat.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                  {errors.tournamentCategory && (
                    <p className="err-msg">{errors.tournamentCategory.message as string}</p>
                  )}
                </div>
              </div>

              <div className="tournament-category-wrapper">
                <div className="category-wrap">
                  <label>Gender</label>
                  <div className="chip-group">
                    {TOURNAMENT_GENDERS.map((g) => (
                      <button
                        type="button"
                        key={g}
                        className={`chip ${genderWatch === g ? "selected" : ""}`}
                        onClick={() => setValue("gender", genderWatch === g ? null : g, { shouldDirty: true })}
                      >
                        {TournamentGenderLabel[g]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group update-tournament-match-days">
                  <label>Country</label>
                  <input
                    className="form-control"
                    list="update-tournament-countries"
                    placeholder="Select country"
                    autoComplete="off"
                    {...register("country")}
                  />
                  <datalist id="update-tournament-countries">
                    {COUNTRY_NAMES.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <div className="toggles update-tournament-toggles">
                <label className="toggle">
                  <span className="label">Lock Roster</span>
                  <input type="checkbox" {...register("isRosterLocked")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Allow Other Players to Edit Score</span>
                  <input type="checkbox" {...register("canEditScoreCard")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Allow Impact Player</span>
                  <input type="checkbox" {...register("isImpactPlayerAllowed")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Enable Bonus Points</span>
                  <input type="checkbox" {...register("bonusPointsEnabled")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Strict Mode</span>
                  <input type="checkbox" {...register("isStrictModeEnabled")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Enable Ticker Automation</span>
                  <input
                    type="checkbox"
                    checked={tickerAutomationEnabled}
                    onChange={() => setTickerAutomationEnabled((v) => !v)}
                  />
                  <span className="slider" />
                </label>
              </div>

              {tickerAutomationEnabled && (tickerQuery.isLoading ? (
                <p className="update-tournament-hint">Loading ticker setup…</p>
              ) : (
                <TournamentTickerConfigSection
                  key={tournament.tournamentId}
                  initial={tickerQuery.data ?? []}
                  onChange={setTickerRows}
                />
              ))}

              <div className="form-group-textarea update-tournament-description">
                <label>
                  Tournament Description <span className="req">*</span>
                </label>
                <textarea
                  rows={5}
                  placeholder="Describe your tournament"
                  className={`textarea form-control ${errors.description ? "field-error" : ""}`}
                  {...register("description")}
                />
                {errors.description && <p className="err-msg">{errors.description.message}</p>}
              </div>

              <div className="form-group update-tournament-banner-row">
                <label>Tournament logo (optional)</label>
                {logoPreviewUrl ? (
                  <div className="banner-preview-wrap">
                    <img src={logoPreviewUrl} alt="New logo preview" className="banner-preview-img banner-preview-img--logo" />
                    <span className="banner-preview-caption">
                      New logo (preview){" "}
                      <button type="button" className="btn-link" onClick={() => clearNewImage("logo")}>
                        Undo
                      </button>
                    </span>
                  </div>
                ) : tournament.logoUri && !removeLogo ? (
                  <div className="banner-preview-wrap">
                    <img
                      src={resolvePublicImageUrl(tournament.logoUri)}
                      alt="Current logo"
                      className="banner-preview-img banner-preview-img--logo"
                    />
                    <span className="banner-preview-caption">
                      Current logo{" "}
                      <button type="button" className="btn-link" onClick={() => setRemoveLogo(true)}>
                        Remove
                      </button>
                    </span>
                  </div>
                ) : removeLogo ? (
                  <span className="banner-preview-caption">
                    Logo will be removed{" "}
                    <button type="button" className="btn-link" onClick={() => setRemoveLogo(false)}>
                      Undo
                    </button>
                  </span>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleImageChange("logo")}
                />
              </div>

              <div className="form-group update-tournament-banner-row">
                <label>Banner image (optional)</label>
                {bannerPreviewUrl ? (
                  <div className="banner-preview-wrap">
                    <img src={bannerPreviewUrl} alt="New banner preview" className="banner-preview-img" />
                    <span className="banner-preview-caption">
                      New banner (preview){" "}
                      <button type="button" className="btn-link" onClick={() => clearNewImage("banner")}>
                        Undo
                      </button>
                    </span>
                  </div>
                ) : tournament.bannerUri && !removeBanner ? (
                  <div className="banner-preview-wrap">
                    <img
                      src={resolvePublicImageUrl(tournament.bannerUri)}
                      alt="Current banner"
                      className="banner-preview-img"
                    />
                    <span className="banner-preview-caption">
                      Current banner{" "}
                      <button type="button" className="btn-link" onClick={() => setRemoveBanner(true)}>
                        Remove
                      </button>
                    </span>
                  </div>
                ) : removeBanner ? (
                  <span className="banner-preview-caption">
                    Banner will be removed{" "}
                    <button type="button" className="btn-link" onClick={() => setRemoveBanner(false)}>
                      Undo
                    </button>
                  </span>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleImageChange("banner")}
                />
              </div>

              <WaiverSettingsSection
                leagueId={effectiveLeagueId}
                waiverRequired={waiverRequired}
                waiverId={waiverId}
                onWaiverRequiredChange={(value) => {
                  setWaiverRequired(value);
                  setValue("waiverRequired", value, { shouldValidate: true, shouldDirty: true });
                  setWaiverError(undefined);
                }}
                onWaiverIdChange={(value) => {
                  setWaiverId(value);
                  setValue("waiverId", value, { shouldValidate: true, shouldDirty: true });
                  setWaiverError(undefined);
                }}
                error={waiverError || (errors.waiverId?.message as string | undefined)}
              />

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Updating…" : "Update Tournament"}
                </button>
              </div>
            </form>
        ) : (
          <p className="update-tournament-modal-missing-league">League context is missing. Close and try again.</p>
        )}
      </div>
    </div>
    </>,
    document.body,
  );
};

export default UpdateTournamentModal;
